import { isProdVercelEnv } from "~/lib/env"

type MetricKind = "gauge" | "sum"

interface BrowserMetric {
  name: string
  value: number
  kind?: MetricKind
  unit?: string
  attributes?: Record<string, string | number | boolean>
}

interface LayoutShiftEntry extends PerformanceEntry {
  value: number
  hadRecentInput: boolean
}

interface LargestContentfulPaintEntry extends PerformanceEntry {
  startTime: number
  size: number
}

interface EventTimingEntry extends PerformanceEntry {
  duration: number
  interactionId?: number
}

const METRICS_ENDPOINT = "/_otel/browser/v1/metrics"
let pendingLargestContentfulPaint: BrowserMetric | undefined
let pendingCumulativeLayoutShift: BrowserMetric | undefined
let pageEndMetricsFlushed = false

const supportsPerformanceObserverEntryType = (type: string): boolean =>
  "PerformanceObserver" in window && PerformanceObserver.supportedEntryTypes.includes(type)

const metricAttributes = (): Record<string, string> => ({
  path: window.location.pathname,
  hostname: window.location.hostname,
  visibility_state: document.visibilityState,
})

const sendMetrics = (metrics: BrowserMetric[]): void => {
  if (!isProdVercelEnv || metrics.length === 0) return

  const body = JSON.stringify({
    source: "browser",
    serviceName: "vidmax-vite",
    environment: "prod",
    metrics,
  })

  void fetch(METRICS_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body,
    keepalive: true,
  }).catch(() => {
    // Metrics must never affect app behavior.
  })
}

const observeLargestContentfulPaint = (): void => {
  if (!supportsPerformanceObserverEntryType("largest-contentful-paint")) return

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries() as LargestContentfulPaintEntry[]
    const latest = entries[entries.length - 1]
    if (!latest) return

    pendingLargestContentfulPaint = {
      name: "web.vitals.lcp",
      value: latest.startTime,
      unit: "ms",
      attributes: {
        ...metricAttributes(),
        size: latest.size,
      },
    }
  })
  observer.observe({ type: "largest-contentful-paint", buffered: true })
}

const observeCumulativeLayoutShift = (): void => {
  if (!supportsPerformanceObserverEntryType("layout-shift")) return

  let cls = 0
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries() as LayoutShiftEntry[]) {
      if (!entry.hadRecentInput) {
        cls += entry.value
      }
    }

    pendingCumulativeLayoutShift = {
      name: "web.vitals.cls",
      value: cls,
      attributes: metricAttributes(),
    }
  })
  observer.observe({ type: "layout-shift", buffered: true })
}

const observeInteractionLatency = (): void => {
  if (!supportsPerformanceObserverEntryType("event")) return

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries() as EventTimingEntry[]) {
      if (entry.interactionId === undefined && !entry.name) continue

      sendMetrics([
        {
          name: "web.vitals.inp_candidate",
          value: entry.duration,
          unit: "ms",
          attributes: {
            ...metricAttributes(),
            event_name: entry.name,
            interaction_id: entry.interactionId ?? 0,
          },
        },
      ])
    }
  })
  observer.observe({ type: "event", buffered: true })
}

const sendNavigationMetrics = (): void => {
  const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined
  if (!navigation) return

  sendMetrics([
    {
      name: "web.navigation.ttfb",
      value: navigation.responseStart,
      unit: "ms",
      attributes: metricAttributes(),
    },
    {
      name: "web.navigation.load",
      value: navigation.loadEventEnd,
      unit: "ms",
      attributes: metricAttributes(),
    },
  ])
}

const observeFirstContentfulPaint = (): void => {
  const paintEntries = performance.getEntriesByType("paint")
  const fcp = paintEntries.find((entry) => entry.name === "first-contentful-paint")
  if (!fcp) return

  sendMetrics([
    {
      name: "web.vitals.fcp",
      value: fcp.startTime,
      unit: "ms",
      attributes: metricAttributes(),
    },
  ])
}

const flushPageEndMetrics = (): void => {
  if (pageEndMetricsFlushed) return
  pageEndMetricsFlushed = true

  sendMetrics(
    [pendingLargestContentfulPaint, pendingCumulativeLayoutShift].filter(
      (metric): metric is BrowserMetric => metric !== undefined,
    ),
  )
  pendingLargestContentfulPaint = undefined
  pendingCumulativeLayoutShift = undefined
}

export const initFrontendMetrics = (): void => {
  if (!isProdVercelEnv || typeof window === "undefined") return

  observeLargestContentfulPaint()
  observeCumulativeLayoutShift()
  observeInteractionLatency()

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushPageEndMetrics()
    }
  })
  window.addEventListener("pagehide", flushPageEndMetrics, { once: true })

  window.addEventListener("load", () => {
    observeFirstContentfulPaint()
    window.setTimeout(sendNavigationMetrics, 0)
  })
}
